import log from '@/lib/logger';
import {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useAuth} from '@/context/use-auth';
import {Birthday, getUpcomingBirthdaysForDashboard} from '@/services/birthday-services';
import {useTranslation} from 'react-i18next';
import {Text} from '@/components/tailwind/text';
import {CakeIcon} from '@heroicons/react/24/outline';

export function UpcomingBirthdaysWidget() {
    const { token } = useAuth();
    const navigate = useNavigate();
    const { t } = useTranslation('common');
    const [birthdays, setBirthdays] = useState<Birthday[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        async function loadBirthdays() {
            if (!token) return;

            setIsLoading(true);
            setError(null);
            try {
                const upcomingBirthdays = await getUpcomingBirthdaysForDashboard(token, 20);
                // Filter only birthdays in the next 5 days (including today)
                const filteredBirthdays = upcomingBirthdays.filter(birthday => {
                    const daysUntil = getDaysUntilBirthday(birthday.dateOfBirth);
                    return daysUntil <= 5;
                });
                setBirthdays(filteredBirthdays);
            } catch (err) {
                log.error("Failed to load upcoming birthdays:", err);
                setError(t('dashboard.widgets.upcomingBirthdays.loadError'));
            } finally {
                setIsLoading(false);
            }
        }

        loadBirthdays();

        // Auto-refresh at midnight
        const now = new Date();
        const tomorrow = new Date(now);
        tomorrow.setDate(tomorrow.getDate() + 1);
        tomorrow.setHours(0, 0, 0, 0);
        const msUntilMidnight = tomorrow.getTime() - now.getTime();

        const timeoutId = setTimeout(() => {
            loadBirthdays();

            // Set up daily refresh
            const intervalId = setInterval(loadBirthdays, 24 * 60 * 60 * 1000);

            return () => clearInterval(intervalId);
        }, msUntilMidnight);

        return () => clearTimeout(timeoutId);
    }, [token, t]);

    function formatDateOfBirth(dateString: string) {
        const date = new Date(dateString);
        return new Intl.DateTimeFormat('de-DE', {
            day: '2-digit',
            month: '2-digit'
        }).format(date);
    }

    function isToday(dateString: string): boolean {
        const today = new Date();
        const date = new Date(dateString);
        return date.getDate() === today.getDate() &&
               date.getMonth() === today.getMonth();
    }

    function getDaysUntilBirthday(dateString: string): number {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const currentYear = today.getFullYear();
        const date = new Date(dateString);
        const birthdayThisYear = new Date(currentYear, date.getMonth(), date.getDate());

        if (birthdayThisYear < today) {
            birthdayThisYear.setFullYear(currentYear + 1);
        }

        const diffTime = Math.abs(birthdayThisYear.getTime() - today.getTime());
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    }

    function handleViewAll() {
        const currentMonth = new Date().getMonth() + 1;
        navigate(`/reports/birthdays?month=${currentMonth}`);
    }

    if (isLoading) {
        return (
            <div className="rounded-lg bg-white dark:bg-gray-900 shadow-sm border border-gray-200 dark:border-gray-800 p-6" data-testid="upcoming-birthdays-widget">
                <div className="animate-pulse">
                    <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-4"></div>
                    <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div
                className="rounded-lg bg-white dark:bg-gray-900 shadow-sm border border-gray-200 dark:border-gray-800 p-6 cursor-pointer hover:shadow-md transition-shadow"
                onClick={handleViewAll}
                data-testid="upcoming-birthdays-widget"
            >
                <div className="flex items-center space-x-2 mb-3">
                    <CakeIcon className="h-5 w-5 text-pink-600 dark:text-pink-400" />
                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                        {t('dashboard.widgets.upcomingBirthdays.title')}
                    </h3>
                </div>
                <div className="text-red-600 dark:text-red-400 text-sm">
                    {error}
                </div>
            </div>
        );
    }

    return (
        <div className="rounded-lg bg-white dark:bg-gray-900 shadow-sm border border-gray-200 dark:border-gray-800 p-6" data-testid="upcoming-birthdays-widget">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                    <CakeIcon className="h-6 w-6 text-pink-600 dark:text-pink-400" />
                    <div>
                        <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                            {t('dashboard.widgets.upcomingBirthdays.title')}
                        </h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            {t('dashboard.widgets.upcomingBirthdays.subtitle')}
                        </p>
                    </div>
                </div>
                <button
                    onClick={handleViewAll}
                    className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline"
                    data-testid="view-all-birthdays"
                >
                    {t('dashboard.widgets.upcomingBirthdays.viewAll')}
                </button>
            </div>

            {birthdays.length === 0 ? (
                <div className="text-center py-3">
                    <Text className="text-gray-500 dark:text-gray-400 text-sm">
                        {t('dashboard.widgets.upcomingBirthdays.noBirthdays')}
                    </Text>
                </div>
            ) : (
                <div className="overflow-x-auto pb-2">
                    <div className="flex gap-3 min-w-max">
                        {birthdays.map((birthday) => {
                            const isBirthdayToday = isToday(birthday.dateOfBirth);
                            const daysUntil = getDaysUntilBirthday(birthday.dateOfBirth);

                            return (
                                <div
                                    key={birthday.id}
                                    className={`flex-shrink-0 p-3 rounded-lg border transition-all hover:shadow-sm w-48 ${
                                        isBirthdayToday
                                            ? 'bg-gradient-to-br from-pink-50 to-purple-50 dark:from-pink-950/30 dark:to-purple-950/30 border-pink-300 dark:border-pink-600'
                                            : birthday.isRoundBirthday
                                                ? 'bg-purple-50 dark:bg-purple-950/20 border-purple-200 dark:border-purple-700'
                                                : birthday.isSpecialAge
                                                    ? 'bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-700'
                                                    : 'bg-gray-50 dark:bg-gray-800/30 border-gray-200 dark:border-gray-700'
                                    }`}
                                >
                                    <div className="flex items-center gap-2 mb-2">
                                        {isBirthdayToday && <span className="text-sm">🎉</span>}
                                        <div className={`font-semibold text-sm truncate ${
                                            isBirthdayToday
                                                ? 'text-pink-700 dark:text-pink-300'
                                                : birthday.isRoundBirthday
                                                    ? 'text-purple-700 dark:text-purple-300'
                                                    : birthday.isSpecialAge
                                                        ? 'text-amber-700 dark:text-amber-300'
                                                        : 'text-gray-900 dark:text-gray-100'
                                        }`}>
                                            {birthday.firstName} {birthday.lastName}
                                        </div>
                                    </div>

                                    <div className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                                        {formatDateOfBirth(birthday.dateOfBirth)}
                                    </div>

                                    <div className="flex items-center justify-between">
                                        <div className="text-xs text-gray-600 dark:text-gray-400">
                                            {t('dashboard.widgets.upcomingBirthdays.turnsAgeShort', { age: birthday.age })}
                                        </div>

                                        <div className="flex items-center gap-1">
                                            {birthday.isRoundBirthday && (
                                                <span className="text-xs">🎊</span>
                                            )}
                                            {birthday.isSpecialAge && !birthday.isRoundBirthday && (
                                                <span className="text-xs">🌟</span>
                                            )}
                                            <div className={`text-xs font-medium px-2 py-1 rounded ${
                                                isBirthdayToday
                                                    ? 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300'
                                                    : daysUntil <= 7
                                                        ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300'
                                                        : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                                            }`}>
                                                {isBirthdayToday
                                                    ? t('dashboard.widgets.upcomingBirthdays.today')
                                                    : daysUntil === 1
                                                        ? t('dashboard.widgets.upcomingBirthdays.tomorrow')
                                                        : t('dashboard.widgets.upcomingBirthdays.daysShort', { days: daysUntil })
                                                }
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}
